import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, FormControl, InputLabel, MenuItem, Select, Snackbar, Alert } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import type { EndpointSort, QuerySort } from '@api/types/performance'
import {
  useFlushMetrics,
  usePerfDatabase,
  usePerfEndpoints,
  usePerfOverview,
  usePerfProbes,
  usePerfQueries,
  usePerfSeries,
  usePerfSlowRequests,
} from '../hooks/usePerformance'
import { OverviewTab } from '../components/OverviewTab'
import { EndpointsTab } from '../components/EndpointsTab'
import { QueriesTab } from '../components/QueriesTab'
import { SlowRequestsTab } from '../components/SlowRequestsTab'
import { DatabaseTab } from '../components/DatabaseTab'
import { ProbesTab } from '../components/ProbesTab'
import { useResetStatementStats } from '../hooks/usePerformance'
import { TIME_WINDOWS } from '../components/format'

const TAB_KEYS = ['overview', 'endpoints', 'queries', 'slow', 'database', 'uptime']
const ENDPOINT_SORTS: EndpointSort[] = ['total', 'p95', 'p99', 'avg', 'db', 'count', 'errors']
const QUERY_SORTS: QuerySort[] = ['total', 'avg', 'p95', 'max', 'count', 'errors']

const TABS: ResponsiveTabItem[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'endpoints', label: 'Endpoints' },
  { key: 'queries', label: 'Queries' },
  { key: 'slow', label: 'Slow requests' },
  { key: 'database', label: 'Database' },
  { key: 'uptime', label: 'Uptime' },
]

/**
 * The performance dashboard.
 *
 * Six views of the same question asked at different distances: what is slow, which endpoint, which
 * query, which single request, what the database itself thinks, and whether any of it is answering
 * at all.
 *
 * The window and the service filter are held here rather than per tab, so moving between tabs keeps
 * the same slice of time in view. Comparing an endpoint's p95 against a query's total time is only
 * meaningful when both cover the same window, and a per-tab control makes that easy to get wrong
 * without noticing.
 *
 * All of it (tab, window, service, sorts, the charted route) lives in the URL, the same way the
 * Crashes page does it, so "p95 of this endpoint over 7 days" is a link that can be pasted into a
 * chat and survives a refresh. Defaults are left out of the URL to keep links short.
 */
export default function PerformancePage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const tabParam = searchParams.get('tab')
  const tab = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'overview'
  const hoursParam = Number(searchParams.get('hours'))
  const hours = TIME_WINDOWS.some((w) => w.hours === hoursParam) ? hoursParam : 24
  const service = searchParams.get('service') ?? ''
  const endpointSort = (ENDPOINT_SORTS.find((x) => x === searchParams.get('esort')) ??
    'total') as EndpointSort
  const querySort = (QUERY_SORTS.find((x) => x === searchParams.get('qsort')) ?? 'total') as QuerySort
  const routeParam = searchParams.get('route')
  const selectedRoute = routeParam ? { route: routeParam, method: searchParams.get('method') ?? 'GET' } : null

  // One writer for every param. Functional update so two writes in the same tick (ResponsiveTabs
  // writes ?tab= and then calls onChange) compose instead of the second clobbering the first.
  const setParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(changes)) {
            if (value == null || value === '') next.delete(key)
            else next.set(key, value)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setTab = useCallback((key: string) => setParams({ tab: key }), [setParams])
  const setHours = (value: number) => setParams({ hours: value === 24 ? null : String(value) })
  const setService = (value: string) => setParams({ service: value })
  const setEndpointSort = (value: EndpointSort) => setParams({ esort: value === 'total' ? null : value })
  const setQuerySort = (value: QuerySort) => setParams({ qsort: value === 'total' ? null : value })
  const setSelectedRoute = (selection: { route: string; method: string } | null) =>
    setParams({ route: selection?.route ?? null, method: selection?.method ?? null })

  const [toast, setToast] = useState<string | null>(null)

  const serviceFilter = service || undefined

  const overview = usePerfOverview(hours, serviceFilter)

  // The overview chart is always every endpoint; the endpoints tab charts whatever is selected.
  // Two separate queries rather than one shared one, because they genuinely ask different things
  // and sharing would make the overview chart change when a row was clicked on another tab.
  const overviewSeries = usePerfSeries(hours, undefined, undefined, serviceFilter)
  const endpointSeries = usePerfSeries(hours, selectedRoute?.route, selectedRoute?.method, serviceFilter)

  const endpoints = usePerfEndpoints(hours, endpointSort, serviceFilter)
  const queries = usePerfQueries(hours, querySort, serviceFilter)
  const slowRequests = usePerfSlowRequests(hours, serviceFilter)

  // Only fetched while its tab is open. It polls every fifteen seconds and runs a handful of
  // catalogue queries each time, which is not something to do in the background on five other tabs.
  const database = usePerfDatabase(tab === 'database')
  const probes = usePerfProbes(hours)

  const flush = useFlushMetrics()
  const resetStatements = useResetStatementStats()

  // One write, so the tab and the route land together and the endpoints tab never renders a frame
  // charting the previous selection.
  const selectRoute = (route: string, method: string) => {
    setParams({ route, method, tab: 'endpoints' })
  }

  const refreshNow = async () => {
    try {
      const result = await flush.mutateAsync()
      setToast(
        `${result.message} The user API flushes on its own timer, so its newest minute may still be up to a minute behind.`
      )
    } catch {
      setToast('Could not flush. Performance collection may be switched off on this host.')
    }
  }

  const windowLabel = TIME_WINDOWS.find((w) => w.hours === hours)?.label ?? `${hours}h`

  return (
    <PageShell
      title="Performance"
      subtitle={`Request and query timings over the last ${windowLabel.toLowerCase()}`}
      // Direct children rather than a nested row: PageShell's action row wraps and stretches its
      // own children, and a non-wrapping inner Stack defeated that and overflowed at 360px.
      actions={
        <>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel id="perf-window">Window</InputLabel>
            <Select
              labelId="perf-window"
              label="Window"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
            >
              {TIME_WINDOWS.map((w) => (
                <MenuItem key={w.hours} value={w.hours}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel id="perf-service">Service</InputLabel>
            <Select
              labelId="perf-service"
              label="Service"
              value={service}
              onChange={(e) => setService(e.target.value)}
            >
              <MenuItem value="">Both</MenuItem>
              {/* A service from a pasted link stays selectable before the overview has loaded. */}
              {Array.from(new Set([...(overview.data?.services ?? []), ...(service ? [service] : [])])).map(
                (s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshNow}
            disabled={flush.isPending}
            sx={{ whiteSpace: 'nowrap', minHeight: 40 }}
          >
            Flush now
          </Button>
        </>
      }
    >
      <ResponsiveTabs tabs={TABS} value={tab} onChange={setTab} sx={{ mb: 2 }} />

      {tab === 'overview' && (
        <OverviewTab
          overview={overview.data}
          series={overviewSeries.data}
          seriesError={overviewSeries.isError}
          hours={hours}
          loading={overview.isLoading}
          error={overview.isError}
          onRetry={() => overview.refetch()}
          onRetrySeries={() => overviewSeries.refetch()}
          onSelectRoute={selectRoute}
        />
      )}

      {tab === 'endpoints' && (
        <EndpointsTab
          rows={endpoints.data}
          loading={endpoints.isLoading}
          error={endpoints.isError ? 'Could not load endpoint timings.' : null}
          onRetry={() => endpoints.refetch()}
          sort={endpointSort}
          onSortChange={setEndpointSort}
          hours={hours}
          selected={selectedRoute}
          onSelect={setSelectedRoute}
          series={endpointSeries.data}
          seriesLoading={endpointSeries.isLoading}
          seriesError={endpointSeries.isError}
          onRetrySeries={() => endpointSeries.refetch()}
        />
      )}

      {tab === 'queries' && (
        <QueriesTab
          rows={queries.data}
          loading={queries.isLoading}
          error={queries.isError ? 'Could not load query timings.' : null}
          onRetry={() => queries.refetch()}
          sort={querySort}
          onSortChange={setQuerySort}
        />
      )}

      {tab === 'slow' && (
        <SlowRequestsTab
          rows={slowRequests.data}
          loading={slowRequests.isLoading}
          error={slowRequests.isError ? 'Could not load slow requests.' : null}
          onRetry={() => slowRequests.refetch()}
        />
      )}

      {tab === 'database' && (
        <DatabaseTab
          data={database.data}
          loading={database.isLoading}
          error={database.isError}
          onRetry={() => database.refetch()}
          onResetStatements={() => {
            resetStatements.mutate(undefined, {
              onSuccess: (r) => setToast(r.message),
              onError: () => setToast('pg_stat_statements is not installed on this server.'),
            })
          }}
          resetting={resetStatements.isPending}
        />
      )}

      {tab === 'uptime' && (
        <ProbesTab
          targets={probes.data}
          loading={probes.isLoading}
          error={probes.isError}
          onRetry={() => probes.refetch()}
          hours={hours}
        />
      )}

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </PageShell>
  )
}
